import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function WorkOrders() {
  const [list, setList] = useState([]);
  const [eqs, setEqs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({ project_id: "", equipment_id: "", wo_number: `JO-${Date.now().toString().slice(-6)}`, type: "preventive", title: "", description: "", priority: "medium", status: "open", due_date: "", requires_permit: false, execution_type: "internal", estimated_hours: 0 });

  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setForm((f) => ({ ...f, project_id: r.data[0].id })); });
    api.get("/equipment").then((r) => setEqs(r.data));
    api.get("/maintenance/kpis").then((r) => setKpi(r.data)).catch(() => {});
  }, []);
  const load = () => api.get("/workorders", { params: statusFilter !== "all" ? { status: statusFilter } : {} }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [statusFilter]);

  const submit = async () => {
    try { await api.post("/workorders", { ...form, estimated_hours: parseFloat(form.estimated_hours) || 0 }); toast.success(form.requires_permit ? "Job Order created — PTW auto-raised in Safety" : "Job Order created"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/workorders/${id}`, { status, completed_date: status === "completed" ? new Date().toISOString().slice(0, 10) : null }); load(); toast.success("Updated"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filt = list.filter((w) => !q || `${w.wo_number} ${w.title} ${w.type}`.toLowerCase().includes(q.toLowerCase()));
  const eqMap = Object.fromEntries(eqs.map((e) => [e.id, e]));

  return (
    <AppLayout title="Job Orders" subtitle="Breakdown, preventive & scheduled maintenance — with PTW integration">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-5">
        {[["MTBF (h)", kpi?.mtbf], ["MTTR (h)", kpi?.mttr], ["Availability %", kpi?.availability], ["PM %", kpi?.pm_percent], ["Cost / MT", kpi?.maintenance_cost_per_mt], ["Open Jobs", kpi?.open_jobs], ["Backlog", kpi?.backlog]].map(([l, v]) => (
          <Card key={l} className="p-3" data-testid={`maint-kpi-${l.split(" ")[0].toLowerCase()}`}><div className="label-cap text-slate-500 text-[10px]">{l}</div><div className="text-xl font-bold mt-0.5">{v ?? "—"}</div></Card>
        ))}
      </div>
      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="New Job Order"
        addTestId="wo-add-button" searchTestId="wo-search-input"
        filters={
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="wo-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", "open", "in_progress", "completed", "closed"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        }
      />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>JO #</TableHead><TableHead>Type</TableHead><TableHead>Title</TableHead>
              <TableHead>Equipment</TableHead><TableHead>Priority</TableHead><TableHead>PTW</TableHead><TableHead>Due</TableHead>
              <TableHead className="text-right">Downtime (h)</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filt.map((w) => (
              <TableRow key={w.id} data-testid={`wo-row-${w.wo_number}`}>
                <TableCell className="font-mono font-semibold">{w.wo_number}</TableCell>
                <TableCell><StatusBadge status={w.type} /></TableCell>
                <TableCell className="max-w-xs truncate">{w.title}</TableCell>
                <TableCell className="text-xs text-slate-500">{eqMap[w.equipment_id]?.code || "—"}</TableCell>
                <TableCell><StatusBadge status={w.priority} /></TableCell>
                <TableCell className="text-xs">{w.requires_permit ? <span className="text-amber-600 font-semibold">Yes</span> : "—"}</TableCell>
                <TableCell className="font-mono text-xs">{w.due_date || "—"}</TableCell>
                <TableCell className="text-right font-mono">{w.downtime_hours}</TableCell>
                <TableCell><StatusBadge status={w.status} /></TableCell>
                <TableCell>
                  {w.status !== "completed" && w.status !== "closed" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(w.id, "completed")} data-testid={`wo-complete-${w.wo_number}`}>Complete</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!filt.length && <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-400">No job orders</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="wo-create-dialog">
          <DialogHeader><DialogTitle>New Job Order</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-cap">JO #</Label><Input data-testid="wo-number-input" value={form.wo_number} onChange={(e) => setForm({ ...form, wo_number: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="wo-type-input"><SelectValue /></SelectTrigger>
                <SelectContent>{["breakdown", "preventive", "scheduled"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="label-cap">Title</Label><Input data-testid="wo-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="wo-project-input"><SelectValue /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-cap">Equipment</Label>
              <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                <SelectTrigger data-testid="wo-equipment-input"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{eqs.filter((e) => !form.project_id || e.project_id === form.project_id).map((e) => <SelectItem key={e.id} value={e.id}>{e.code} · {e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-cap">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="wo-priority-input"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-cap">Execution</Label>
              <Select value={form.execution_type} onValueChange={(v) => setForm({ ...form, execution_type: v })}>
                <SelectTrigger data-testid="wo-execution-input"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="internal">Internal</SelectItem><SelectItem value="contractor">Contractor</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Est. Hours</Label><Input type="number" data-testid="wo-esthours-input" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} /></div>
            <div><Label className="label-cap">Due Date</Label><Input type="date" data-testid="wo-due-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm border rounded-md px-3 py-2 bg-amber-50">
              <Checkbox checked={form.requires_permit} onCheckedChange={(v) => setForm({ ...form, requires_permit: !!v })} data-testid="wo-permit-input" />
              Requires Permit to Work (auto-raises a PTW request in Safety for approval)
            </label>
            <div className="col-span-2"><Label className="label-cap">Description</Label><Textarea data-testid="wo-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="wo-submit-button">Create Job Order</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
